/**
 * =============================================================================
 * variant-state-manager.js
 * =============================================================================
 * Manage state of variants / variant buttons for a given product form
 * 
 * TODO: sticky buy bar update price
 * =============================================================================
 */

 class VariantStateManager {
  // The HTML elements, part of the product form
  parentContainer;
  variantSelect;
  sellingPlanSelect;

  proxyVariantOptionPickers;

  variants;
  currentOptions;
  currentVariant;
  currentSellingPlan;

  constructor(parentContainer, variantSelect, sellingPlanSelect) {
    this.parentContainer = parentContainer
    this.variantSelect = variantSelect;
    this.sellingPlanSelect = sellingPlanSelect;

    if(this.sellingPlanSelect){
      this.sellingPlanSelect.addEventListener("change", this.onVariantChange.bind(this));
    }

    if(this.variantSelect) {
      this.variantSelect.addEventListener('change', this.onVariantChange.bind(this));

      this.setupInitialState();

      // Handle variant change via non-standard option pickers
      this.proxyVariantOptionPickers = this.parentContainer.querySelectorAll("[data-variant-option-picker]");
      this.proxyVariantOptionPickers.forEach(element => {
        if(element.getAttribute("data-variant-option-picker") == "change"){
          element.addEventListener("change", this.onProxyOptionChange.bind(this));
        } else {
          element.addEventListener("click", this.onProxyOptionClick.bind(this));
        }
      });
    }
  }

  /**
   * Handle change event on a proxy option picker (e.g. the 'select' elements in
   * the sticky buy bar)
   * 
   * @param {Event} event 
   */
  onProxyOptionChange(event) {
    let optionName = event.target.options[event.target.selectedIndex].dataset.option;
    let optionValue = event.target.options[event.target.selectedIndex].dataset.value;
    this.clickOptionButton(optionName, optionValue);
  }

  /**
   * Handle click event on a proxy option picker (e.g. the colour swatches
   * container)
   * @param {Event} event 
   */
  onProxyOptionClick(event) {
    this.clickOptionButton(event.target.dataset.option, event.target.dataset.value);
  }

  /**
   * Click the appropriate variant option radio button, if it exists
   * 
   * @param {String} name 
   * @param {String} value 
   */
  clickOptionButton(name, value) {
    let optionButton = this.variantSelect.querySelector(`[name='${name}'][value='${value}']`);
    if(optionButton){
      optionButton.click();
    }
  }

  /**
   * Update any proxy option selects when the variant changes. This isn't
   * required for elements with the [data-rerender-on-variant-change] attribute
   */
  updateProxyOptionSelects() {
    this.proxyVariantOptionPickers.forEach(element => {
      let optionSelects =  element.querySelectorAll("select");
      optionSelects.forEach((element, index) => {
        element.value = this.currentOptions[index];
        let event = document.createEvent('HTMLEvents');
        event.initEvent('change', false, true);
        element.dispatchEvent(event);
      });
    });
  }

  /**
   * Configure initial state of variant select
   */
  setupInitialState() {
    this.updateOptions();
    this.updateCurrentVariant();
    this.updateCurrentSellingPlan();
    //this.updateOptionState(this.currentOptions[0]);
  }

  /**
   * Handle variant change
   * 
   * @param {Event} e The change event
   */
  onVariantChange(e) {
    this.updateOptions();
    this.updateCurrentVariant();
    this.updateCurrentSellingPlan();
    this.updateAddButtons(true, "");

    if (!this.currentVariant) {
      // No variant that matches the selected options
      this.updateAddButtons(true, '', true);
      this.setUnavailable();
    } else {
      // Variant found matching those options.
      var event = new CustomEvent("variant:changed", { detail: {
        variant: this.currentVariant
      }});
      this.parentContainer.dispatchEvent(event);
      
      //this.updateOptionState(e.target.value);
      this.updateURL();
      this.updateVariantInput();
      this.fetchAndRenderProductInfo();
    }

    this.updateProxyOptionSelects();
  }

  /**
   * Update the currently selected variant ID in the product page's hidden
   * inputs. Used when a form is submitted.
   */
  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.variantSelect.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * Once a variant changes, get the new variant info by fetching it, extracting
   * the new price panel, and replacing the price panel with the fetched one
   */
  fetchAndRenderProductInfo() {
    let url = `${this.variantSelect.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.variantSelect.dataset.section}`;
    if(this.currentSellingPlan){
      url = `${url}&selling_plan=${this.currentSellingPlan}`;
    }
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')

        // 'html' is the entirety of the product-main section. We can parse out
        // the bits we want to update on variant change
        let elementsToRerender = this.parentContainer.querySelectorAll("[data-rerender-on-variant-change]");

        elementsToRerender.forEach(element => {
          if(element.id){
            let rerenderedElement = html.getElementById(element.id);
            if(rerenderedElement){
              element.innerHTML = rerenderedElement.innerHTML;
            }
          } else {
            console.warn("[V-S-M] Tried to re-render element, but had no ID!");
          }
        });

        let elementsToHideOnUnavailable = this.parentContainer.querySelectorAll("[data-hide-when-unavailable]");
        elementsToHideOnUnavailable.forEach(element => {
          element.classList.remove("invisible");  
        });

        this.updateAddButtons(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  /**
   * Set / update options array to the currently selected options (which may not
   * necessarily match a variant)
   */
  updateOptions() {
    const fieldsets = Array.from(this.variantSelect.querySelectorAll('fieldset'));
    this.currentOptions = fieldsets.map(fieldset => {
      let checked = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      if(checked){
        return checked.value;
      } else {
        console.warn("No value checked in the fieldset? ", fieldset);
      }
    });
  }

  /**
   * Set / update the currently selected variant, or false if the current 
   * options don't match a variant
   */
  updateCurrentVariant() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.currentOptions[index] === option;
      }).includes(false);
    });
  }

  /**
   * Set / update the currently selected selling plan
   */
  updateCurrentSellingPlan() {
    if(this.sellingPlanSelect){
      this.currentSellingPlan = this.sellingPlanSelect.value;
    }
  }

  /**
   * Get the current product's variants
   * @returns {Object} The product's variants
   */
  getVariantData() {
    this.variants = this.variants || JSON.parse(this.variantSelect.querySelector('[type="application/json"]').textContent);
    return this.variants;
  }

  /**
   * Set the state of the add button
   * 
   * @param {*} disable Whether to disable the button
   * @param {*} text Text to set, if disable is set to 'true' - don't like this.
   * @param {*} modifyClass No use
   * @returns 
   */
  updateAddButtons(disable = true, text, modifyClass = true) {
    const addButtons = this.parentContainer.querySelectorAll('[name="add"]');
    addButtons.forEach(addButton => {
      const addButtonText = addButton.querySelector('span');
      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        if(this.currentVariant.tags?.includes("Config: Buyable Preorder")){
          addButtonText.textContent = window.variantStrings.preorder;
        } else {
          addButtonText.textContent = window.variantStrings.addToCart;
        }
      }
      if (!modifyClass) return;
    });
  }

  /**
   * Update the URL based on the new variant ID with any prior query parameters
   */
  updateURL() {
    if (!this.currentVariant || this.variantSelect.dataset.updateUrl === 'false') return;

    // Get existing params (if any, e.g. 'view', and update with variant ID)
    const urlSearchParams = new URLSearchParams(window.location.search);
    let params = Object.fromEntries(urlSearchParams.entries());
    params["variant"] = this.currentVariant.id;
    if(this.currentSellingPlan){
      params["selling_plan"] = this.currentSellingPlan;
    } else {
      delete params["selling_plan"];
    }
    const paramString = Object.keys(params).map((key) => [key, params[key]].join("=")).join("&")

    // Set new URL based on prior query string updated with new variant ID
    window.history.replaceState({}, '', `${this.variantSelect.dataset.url}?${paramString}`);
  }

  /**
   * Update the option buttons state, depending on how many option groups there
   * are (max 3 as per Shopify)
   * @param {string} selectedOption 
   */
  updateOptionState(selectedOption) {
    //console.log(`[VSM] Setting state for option '${selectedOption}'`);
    if(this.currentOptions.length === 1){
      // 1 product option - simply disable any sold out options
      this.update1OptionState();
    } else if(this.currentOptions.length === 2){
      // 2 product options - disable any on the opposing pair
      this.update2OptionState(selectedOption);
    } else {
      // Do nothing - can't manage state of 3 product options in a meaningful way
    }
  }

  /**
   * Disable / enabled buttons based on the last selected option. For single
   * option products this won't change, as a given variant is either available,
   * or it's not.
   */
  update1OptionState() {
    const fieldset = this.variantSelect.querySelector('fieldset');
    const radioButtons = Array.from(fieldset.querySelectorAll('input'));

    // Check if the given variant (as it's a 1-1 match is available)
    radioButtons.forEach(radio => {
      let matchedVariant = this.variants.find(variant => variant.option1 === radio.value);
      if(!matchedVariant || !matchedVariant.available){
        radio.setAttribute('disabled', 'disabled');
      } else {
        radio.removeAttribute('disabled');
      }
    });
  }

  /**
   * Disable / enable buttons based on the last selected option. For 2 option
   * products this will disable any values for the 'other' option where the 2
   * would result in an out-of-stock or unavailable selection.
   * 
   * @param {string} selectedOption The option that was selected
   */
  update2OptionState(selectedOption) {
    const fieldsets = Array.from(this.variantSelect.querySelectorAll('fieldset'));
    const optionIndex = this.currentOptions.indexOf(selectedOption);
    const otherOptionIndex = 1 - optionIndex;
    const otherOptionRadioButtons = Array.from(fieldsets[otherOptionIndex].querySelectorAll('input'));
    otherOptionRadioButtons.forEach(radio => {
      let matchedVariant = this.variants.find(variant => {
        return variant.options[optionIndex] == selectedOption && 
        variant.options[otherOptionIndex] == radio.value
      });
      if(!matchedVariant || !matchedVariant.available){
        radio.setAttribute('disabled', 'disabled');
      } else {
        radio.removeAttribute('disabled');
      }
    });
  }

  /**
   * Update the visual state (e.g. button text, price visibility) when the
   * options selected don't match a variant (i.e. it's unavailable)
   */
  setUnavailable() {
    const addButtons = this.parentContainer.querySelectorAll('[name="add"]');
    addButtons.forEach(addButton => {
      const addButtonText = addButton.querySelector('span');
      addButtonText.textContent = window.variantStrings.unavailable;
    });

    let elementsToHideOnUnavailable = this.parentContainer.querySelectorAll("[data-hide-when-unavailable]");
    elementsToHideOnUnavailable.forEach(element => {
      element.classList.add("invisible");  
    });
  }
}

export default VariantStateManager